'use client'

import { useState, FormEvent, useRef } from 'react'
import { X, Save, Upload, User, Image as ImageIcon } from 'lucide-react'
import { panel, panelInner, headingClass, mutedClass, ghostBtn, primaryBtn, primaryBtnInline, fieldClass, labelClass, errorBox, successBox } from './ui'

interface Manager {
  id: string
  name: string
  email: string
  phone: string | null
  photoUrl: string | null
  isActive: boolean
}

interface ManagerFormProps {
  manager: Manager | null
  userId: string
  onSuccess: () => void
  onCancel: () => void
}

export default function ManagerForm({ manager, userId, onSuccess, onCancel }: ManagerFormProps) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [formData, setFormData] = useState({
    name: manager?.name || '',
    pronom: (manager as any)?.pronom || '',
    email: manager?.email || '',
    phone: manager?.phone || '',
    username: (manager as any)?.username || '',
    password: '',
    is_active: manager?.isActive !== undefined ? manager.isActive : true,
  })
  const [showCredentials, setShowCredentials] = useState(false)
  const [createdCredentials, setCreatedCredentials] = useState<{ username: string; password: string } | null>(null)
  const [photoUrl, setPhotoUrl] = useState<string | null>(manager?.photoUrl || null)
  const [uploadingPhoto, setUploadingPhoto] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    if (!formData.name || !formData.email) {
      setError('Name and email are required')
      setLoading(false)
      return
    }

    // For new managers, username and password are required
    if (!manager && (!formData.username || !formData.password)) {
      setError('Username and password are required for new managers')
      setLoading(false)
      return
    }

    try {
      const url = manager ? `/api/brief613/managers/${manager.id}` : '/api/brief613/managers'
      const method = manager ? 'PATCH' : 'POST'

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
          'x-user-role': 'admin',
        },
        body: JSON.stringify({
          name: formData.name,
          pronom: formData.pronom || null,
          email: formData.email,
          phone: formData.phone || null,
          username: formData.username,
          password: formData.password ? String(formData.password).trim() : undefined,
          new_password: formData.password ? String(formData.password).trim() : undefined,
          photo_url: photoUrl || null,
          is_active: formData.is_active,
        }),
      })

      const data = await response.json()

      if (data.success) {
        // If this is a new manager, show credentials
        if (!manager && data.credentials) {
          setCreatedCredentials(data.credentials)
          setShowCredentials(true)
        } else {
          if (data.passwordUpdated) {
            setError('') // clear any previous error
            setFormData((prev) => ({ ...prev, password: '' })) // clear password field after successful update
          }
          onSuccess()
        }
      } else {
        setError(data.error || 'Failed to save manager')
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred')
    } finally {
      setLoading(false)
    }
  }

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file')
      return
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB')
      return
    }

    setUploadingPhoto(true)
    setError('')

    try {
      const uploadFormData = new FormData()
      uploadFormData.append('file', file)
      uploadFormData.append('type', 'managers')

      const response = await fetch('/api/brief613/upload', {
        method: 'POST',
        body: uploadFormData,
      })

      const data = await response.json()

      if (data.success) {
        setPhotoUrl(data.url)
      } else {
        setError(data.error || 'Failed to upload photo')
      }
    } catch (err: any) {
      setError(err.message || 'Failed to upload photo')
    } finally {
      setUploadingPhoto(false)
    }
  }

  const handleRemovePhoto = () => {
    setPhotoUrl(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  return (
    <div className={`${panel} p-8 max-w-2xl mx-auto`}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-base font-semibold text-white/90 sm:text-lg">{manager ? 'Edit Manager' : 'Add Project Manager'}</h2>
        <button onClick={onCancel} className="text-white/60 hover:text-white transition-colors">
          <X size={24} className="text-white/50" />
        </button>
      </div>

      {error && (
        <div className={`mb-6 ${errorBox}`}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Photo Upload Section */}
        <div>
          <label className={labelClass}>Photo</label>
          <div className="flex items-center gap-4">
            <div className="relative">
              {photoUrl ? (
                <div className="relative group">
                  <img
                    src={photoUrl}
                    alt="Manager photo"
                    className="w-24 h-24 rounded-full object-cover border border-white/10"
                  />
                  <button
                    type="button"
                    onClick={handleRemovePhoto}
                    className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 rounded-full flex items-center justify-center transition-opacity"
                    disabled={uploadingPhoto || loading}
                  >
                    <X size={20} className="text-white" />
                  </button>
                </div>
              ) : (
                <div className="w-24 h-24 rounded-full bg-white/10 border-2 border-white/20 flex items-center justify-center">
                  <User size={40} className="text-white/40" />
                </div>
              )}
            </div>
            <div className="flex-1">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                disabled={uploadingPhoto || loading}
                className="hidden"
                id="photo-upload"
              />
              <label
                htmlFor="photo-upload"
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-colors ${
                  uploadingPhoto || loading
                    ? 'bg-white/5 border-white/10 text-white/40 cursor-not-allowed'
                    : 'bg-white/5 border-white/20 text-white/70 hover:bg-white/10 hover:text-white'
                }`}
              >
                {uploadingPhoto ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/40 border-t-transparent rounded-full animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload size={18} className="text-white/50" />
                    {photoUrl ? 'Change Photo' : 'Upload Photo'}
                  </>
                )}
              </label>
              <p className="text-xs text-white/50 mt-1">JPG, PNG or GIF (max 5MB)</p>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className={fieldClass}
              disabled={loading}
            />
          </div>

          <div>
            <label className={labelClass}>Pronom</label>
            <select
              value={formData.pronom}
              onChange={(e) => setFormData({ ...formData, pronom: e.target.value })}
              className={fieldClass}
              disabled={loading}
            >
              <option value="">Sélectionner</option>
              <option value="Monsieur">Monsieur</option>
              <option value="Madame">Madame</option>
              <option value="M.">M.</option>
              <option value="Mme">Mme</option>
              <option value="Mlle">Mlle</option>
            </select>
          </div>
        </div>

        <div>
          <label className={labelClass}>Email *</label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
            className={fieldClass}
            disabled={loading}
          />
        </div>

        <div>
          <label className={labelClass}>Phone</label>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            className={fieldClass}
            disabled={loading}
          />
        </div>

        {!manager && (
          <>
            <div>
              <label className={labelClass}>Username *</label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                required={!manager}
                className={fieldClass}
                disabled={loading}
                placeholder="Login username for manager"
              />
            </div>

            <div>
              <label className={labelClass}>
                Password {manager ? '(leave empty to keep current)' : '*'}
              </label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required={!manager}
                className={fieldClass}
                disabled={loading}
                placeholder="Password for manager login"
              />
            </div>
          </>
        )}

        {manager && (
          <>
            <div>
              <label className={labelClass}>Username</label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                className={fieldClass}
                disabled={loading}
              />
            </div>

            <div>
              <label className={labelClass}>New Password (leave empty to keep current)</label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className={fieldClass}
                disabled={loading}
                placeholder="Enter new password to change"
              />
            </div>
          </>
        )}

        <div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="rounded"
              disabled={loading}
            />
            <span className="text-white/90">Active</span>
          </label>
        </div>

        {showCredentials && createdCredentials && (
          <div className="p-4 bg-green-500/20 border border-green-500/40 rounded-lg">
            <h3 className="text-lg font-semibold text-green-400 mb-2">Manager Created Successfully!</h3>
            <p className="text-white/90 mb-3">Please save these credentials:</p>
            <div className="space-y-2 mb-4">
              <div>
                <span className="text-white/70 text-sm">Username: </span>
                <span className="font-mono text-white font-semibold">{createdCredentials.username}</span>
              </div>
              <div>
                <span className="text-white/70 text-sm">Password: </span>
                <span className="font-mono text-white font-semibold">{createdCredentials.password}</span>
              </div>
            </div>
            <p className="text-yellow-400 text-sm">⚠️ Save these credentials now - the password cannot be retrieved later!</p>
          </div>
        )}

        <div className="flex justify-end gap-4">
          {showCredentials ? (
            <button
              type="button"
              onClick={() => {
                setShowCredentials(false)
                setCreatedCredentials(null)
                onSuccess()
              }}
              className={primaryBtnInline}
            >
              Done
            </button>
          ) : (
            <>
              <button type="button" onClick={onCancel} className={ghostBtn} disabled={loading}>
                Cancel
              </button>
              <button type="submit" className={primaryBtnInline} disabled={loading}>
                {loading ? 'Saving...' : (
                  <>
                    <Save size={18} className="text-white/50" />
                    {manager ? 'Update Manager' : 'Add Manager'}
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </form>
    </div>
  )
}

